import React, { useState, useEffect, useMemo } from 'react';
import { TextField, Select, MenuItem, FormControlLabel, Switch, Button, Box, Typography, InputLabel, FormControl, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemText, IconButton, Tooltip, Input, Checkbox, Snackbar, Autocomplete, CircularProgress, Divider } from '@mui/material';
import MuiAlert, { AlertProps } from '@mui/material/Alert';
import { SelectChangeEvent } from '@mui/material/Select';
import { Add, Save, Cancel, Edit, Delete, Settings, Attachment, Share, AccountBalance } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { Expense } from '../types/Expense';
import { BankAccount } from '../types/BankAccount';
import { uploadFile } from '../utils/fileUpload';
import { addExpense, getBankAccounts } from '../firebase/firestoreOperations';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useIntl, FormattedMessage } from 'react-intl';
import { ChromePicker } from 'react-color';
import CustomChip from './CustomChip';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { auth } from '../firebase/config';
import { designTokens } from '../theme/designTokens';
import { getCategoryIcon, availableIconNames, iconNameMap } from '../utils/categoryIcons';
import { identifyBrand, getLogoUrl, BrandInfo, normalizeDescription } from '../utils/brandUtils';

interface ExpenseFormProps {
  onAddExpense: (expense: Omit<Expense, 'id' | 'userId'>, file?: File) => Promise<void>;
  categories: string[];
  payers: string[];
  onAddCategory: (category: string) => void;
  onEditCategory: (oldCategory: string, newCategory: string) => void;
  onDeleteCategory: (category: string) => void;
  onAddPayer: (payer: string) => void;
  onEditPayer: (oldPayer: string, newPayer: string) => void;
  onDeletePayer: (payer: string) => void;
  onEditPayerColor: (payer: string, color: string) => void;
  payerColors: { [key: string]: string };
  categoryIcons: { [key: string]: string };
  onEditCategoryIcon: (category: string, iconName: string) => void;
  expenses?: Expense[];
}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
  props,
  ref,
) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const ExpenseForm: React.FC<ExpenseFormProps> = ({ onAddExpense, categories, payers, onAddCategory, onEditCategory, onDeleteCategory, onAddPayer, onEditPayer, onDeletePayer, onEditPayerColor, payerColors, categoryIcons, onEditCategoryIcon, expenses = [] }) => {
  const [expense, setExpense] = useState({
    description: '',
    date: '',
    payer: '',
    category: '',
    shared: false,
    sharedWith: '',
    amount: '',
    bankAccountId: '',
  });
  const [newCategory, setNewCategory] = useState('');
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [editingCategory, setEditingCategory] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortedCategories, setSortedCategories] = useState<string[]>([]);
  const [newPayer, setNewPayer] = useState('');
  const [isAddingPayer, setIsAddingPayer] = useState(false);
  const [editingPayer, setEditingPayer] = useState<string | null>(null);
  const intl = useIntl();

  const [openPayerDialog, setOpenPayerDialog] = useState(false);
  const [editingPayerName, setEditingPayerName] = useState('');
  const [newPayerName, setNewPayerName] = useState('');
  const [openColorPicker, setOpenColorPicker] = useState(false);
  const [selectedPayer, setSelectedPayer] = useState('');
  const [selectedColor, setSelectedColor] = useState('#000000');
  const [openIconPicker, setOpenIconPicker] = useState(false);
  const [selectedCategoryForIcon, setSelectedCategoryForIcon] = useState<string>('');

  const [isDebt, setIsDebt] = useState(false);
  const [debtFrom, setDebtFrom] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [identifiedBrand, setIdentifiedBrand] = useState<BrandInfo | null>(null);

  const [descriptionSuggestions, setDescriptionSuggestions] = useState<string[]>([]);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);

  const longestPayerName = useMemo(() => {
    return payers.reduce((longest, current) =>
      current.length > longest.length ? current : longest
      , '');
  }, [payers]);

  useEffect(() => {
    setSortedCategories([...categories].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })));
  }, [categories]);

  const sortedPayers = useMemo(() => {
    const preferredOrder = ['Bruno', 'Simone', 'Bárbara'];
    return [
      ...preferredOrder.filter(payer => payers.includes(payer)),
      ...payers.filter(payer => !preferredOrder.includes(payer)).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
    ];
  }, [payers]);

  useEffect(() => {
    // Load description suggestions from localStorage
    const savedSuggestions = localStorage.getItem('expenseDescriptionSuggestions');
    let mergedSuggestions: string[] = [];

    if (savedSuggestions) {
      mergedSuggestions = JSON.parse(savedSuggestions);
    }

    // Add suggestions from existing expenses
    if (expenses && expenses.length > 0) {
      const historySuggestions = Array.from(new Set(expenses.map(e => e.description)))
        .filter(desc => desc && desc.trim() !== '');

      // Combine and remove duplicates
      mergedSuggestions = Array.from(new Set([...mergedSuggestions, ...historySuggestions]));
    }

    if (mergedSuggestions.length > 0) {
      setDescriptionSuggestions(mergedSuggestions.sort((a, b) => a.localeCompare(b)));
    }

    // Fetch bank accounts
    const fetchBankAccounts = async () => {
      if (auth.currentUser) {
        try {
          const accounts = await getBankAccounts(auth.currentUser.uid);
          setBankAccounts(accounts);
        } catch (error) {
          console.error('Error fetching bank accounts:', error);
        }
      }
    };
    fetchBankAccounts();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    if (name === 'category' && value === 'new') {
      setIsAddingCategory(true);
    } else if (name === 'payer' && value === 'new') {
      setIsAddingPayer(true);
    } else {
      setExpense(prev => ({
        ...prev,
        [name]: name === 'shared' ? (e.target as HTMLInputElement).checked : value,
      }));
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
    }
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Permite números, um separador decimal (vírgula ou ponto) e até duas casas decimais
    // Substitui a vírgula por ponto para garantir consistência
    const sanitizedValue = value.replace(',', '.');
    if (/^\d*\.?\d{0,2}$/.test(sanitizedValue)) {
      setExpense(prev => ({ ...prev, amount: sanitizedValue }));
    }
  };

  const formatAmount = (value: string) => {
    // Substitui vírgulas por pontos para cálculos internos
    return value.replace(',', '.');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!expense.description || !expense.date || !expense.payer || !expense.category || parseFloat(formatAmount(expense.amount)) <= 0) {
      alert(intl.formatMessage({ id: 'fillAllFields' }));
      return;
    }

    try {
      const newExpense: Omit<Expense, 'id' | 'userId'> = {
        description: normalizeDescription(expense.description),
        date: expense.date,
        payer: isDebt ? debtFrom : expense.payer,
        category: expense.category,
        shared: expense.shared,
        sharedWith: expense.shared ? expense.sharedWith || '' : undefined,
        amount: parseFloat(formatAmount(expense.amount)),
        debtFrom: isDebt ? expense.payer : null,
        isDebt: isDebt,
        bankAccountId: expense.bankAccountId || undefined,
      };

      await onAddExpense(newExpense, selectedFile || undefined);
      setExpense({
        description: '',
        date: new Date().toISOString().split('T')[0],
        payer: '',
        category: '',
        shared: false,
        sharedWith: '',
        amount: '',
        bankAccountId: '',
      });
      setSelectedFile(null);
      setIsDebt(false);
      setDebtFrom('');
      setOpenSnackbar(true); // Abre o Snackbar após submissão bem-sucedida
    } catch (error) {
      console.error('Error adding expense:', error);
      alert(intl.formatMessage({ id: 'errorAddingExpense' }));
    }
  };

  const handleEditCategory = (category: string) => {
    setEditingCategory(category);
    setNewCategory(category);
  };

  const handleSaveEditedCategory = () => {
    const trimmedCategory = newCategory.trim();
    if (editingCategory && trimmedCategory !== '' && trimmedCategory !== editingCategory) {
      if (!categories.includes(trimmedCategory)) {
        onEditCategory(editingCategory, trimmedCategory);
        setExpense(prev => ({
          ...prev,
          category: prev.category === editingCategory ? trimmedCategory : prev.category
        }));
        setEditingCategory(null);
        setNewCategory('');
      } else {
        alert(`${intl.formatMessage({ id: 'categoryExists' })} "${trimmedCategory}".`);
      }
    } else {
      setEditingCategory(null);
      setNewCategory('');
    }
  };

  const handleAddCategory = () => {
    const trimmedCategory = newCategory.trim();
    if (trimmedCategory !== '') {
      if (!categories.includes(trimmedCategory)) {
        onAddCategory(trimmedCategory);
        setIsAddingCategory(false);
        setExpense({ ...expense, category: trimmedCategory });
        setNewCategory('');
      } else {
        alert(`${intl.formatMessage({ id: 'categoryExists' })} "${trimmedCategory}".`);
      }
    }
  };

  const handleAddPayer = () => {
    const trimmedPayer = newPayer.trim();
    if (trimmedPayer !== '') {
      if (!payers.includes(trimmedPayer)) {
        onAddPayer(trimmedPayer);
        setIsAddingPayer(false);
        setExpense({ ...expense, payer: trimmedPayer });
        setNewPayer('');
      } else {
        alert(`${intl.formatMessage({ id: 'payerExists' })} "${trimmedPayer}".`);
      }
    }
  };

  const handleEditPayer = (payer: string) => {
    setEditingPayer(payer);
    setNewPayer(payer);
  };

  const handleSaveEditedPayer = () => {
    if (editingPayer && newPayer.trim() !== '' && newPayer !== editingPayer) {
      onEditPayer(editingPayer, newPayer.trim());
      setExpense(prev => ({
        ...prev,
        payer: prev.payer === editingPayer ? newPayer.trim() : prev.payer,
        sharedWith: prev.sharedWith === editingPayer ? newPayer.trim() : prev.sharedWith
      }));
    }
    setEditingPayer(null);
    setNewPayer('');
  };

  const handleDeleteCategory = (category: string) => {
    if (window.confirm(`${intl.formatMessage({ id: 'confirmDelete' })} "${category}"?`)) {
      onDeleteCategory(category);
      if (expense.category === category) {
        setExpense(prev => ({ ...prev, category: '' }));
      }
    }
  };

  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [editingCategoryName, setEditingCategoryName] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');

  const handleOpenCategoryDialog = () => {
    setOpenCategoryDialog(true);
  };

  const handleCloseCategoryDialog = () => {
    setOpenCategoryDialog(false);
    setEditingCategoryName('');
    setNewCategoryName('');
  };

  const handleEditCategoryStart = (category: string) => {
    setEditingCategoryName(category);
    setNewCategoryName(category);
  };

  const handleEditCategoryConfirm = () => {
    if (newCategoryName.trim() !== '' && newCategoryName !== editingCategoryName) {
      onEditCategory(editingCategoryName, newCategoryName.trim());
      setEditingCategoryName('');
      setNewCategoryName('');
    }
  };

  const handleDeleteCategoryConfirm = (category: string) => {
    if (window.confirm(`${intl.formatMessage({ id: 'confirmDelete' })} "${category}"?`)) {
      onDeleteCategory(category);
    }
  };

  const handleDateChange = (newValue: Date | null) => {
    if (newValue instanceof Date) {
      // Ensure the date is not affected by timezone
      const localDate = new Date(newValue.getTime() - newValue.getTimezoneOffset() * 60000);
      setExpense(prev => ({ ...prev, date: localDate.toISOString().split('T')[0] }));
    } else if (newValue === null) {
      setExpense(prev => ({ ...prev, date: '' }));
    }
  };

  const handleOpenPayerDialog = () => {
    setOpenPayerDialog(true);
  };

  const handleClosePayerDialog = () => {
    setOpenPayerDialog(false);
    setEditingPayerName('');
    setNewPayerName('');
  };

  const handleEditPayerStart = (payer: string) => {
    setEditingPayerName(payer);
    setNewPayerName(payer);
  };

  const handleEditPayerConfirm = () => {
    if (newPayerName.trim() !== '' && newPayerName !== editingPayerName) {
      onEditPayer(editingPayerName, newPayerName.trim());
      setEditingPayerName('');
      setNewPayerName('');
    }
  };

  const handleDeletePayerConfirm = (payer: string) => {
    if (window.confirm(`${intl.formatMessage({ id: 'confirmDelete' })} "${payer}"?`)) {
      onDeletePayer(payer);
    }
  };

  const handleOpenColorPicker = (payer: string) => {
    setSelectedPayer(payer);
    setSelectedColor(payerColors[payer] || '#000000');
    setOpenColorPicker(true);
  };

  const handleCloseColorPicker = () => {
    setOpenColorPicker(false);
  };

  const handleColorChange = (color: any) => {
    setSelectedColor(color.hex);
  };

  const handleColorChangeComplete = () => {
    onEditPayerColor(selectedPayer, selectedColor);
    setOpenColorPicker(false);
  };

  const handleCloseSnackbar = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };

  const filterOptions = createFilterOptions({
    matchFrom: 'start',
    stringify: (option: string) => option,
  });

  const handleDescriptionChange = (event: React.SyntheticEvent, newValue: string | null) => {
    if (typeof newValue === 'string') {
      setExpense(prev => ({ ...prev, description: newValue }));

      // Update suggestions
      if (!descriptionSuggestions.includes(newValue)) {
        const newSuggestions = [...descriptionSuggestions, newValue].slice(-20); // Keep last 20 suggestions
        setDescriptionSuggestions(newSuggestions);
        localStorage.setItem('expenseDescriptionSuggestions', JSON.stringify(newSuggestions));
      }
    }
  };

  const handleSharedWithChange = (event: SelectChangeEvent<string>) => {
    setExpense(prev => ({ ...prev, sharedWith: event.target.value }));
  };

  const handleDebtFromChange = (event: SelectChangeEvent<string>) => {
    setDebtFrom(event.target.value);
  };

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      // Adicione aqui qualquer lógica de carregamento necessária
      setIsLoading(false);
    };

    loadData();
  }, []);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box component="form" onSubmit={handleSubmit} sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
          p: designTokens.spacing.lg,
          background: designTokens.colors.background.paper,
          borderRadius: designTokens.borderRadius.lg,
          border: designTokens.glassmorphism.border,
          boxShadow: designTokens.shadows.md,
          maxWidth: 600,
          mx: 'auto'
        }}>
          <Typography variant="h5" sx={{ fontWeight: designTokens.typography.fontWeight.bold, mb: 1 }}>
            <FormattedMessage id="addExpense" />
          </Typography>

          {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormControl fullWidth>
              <InputLabel id="category-label"><FormattedMessage id="category" /></InputLabel>
              <Select
                labelId="category-label"
                label={intl.formatMessage({ id: 'category' })}
                name="category"
                value={expense.category}
                onChange={handleChange}
                required
                sx={{ borderRadius: designTokens.borderRadius.md }}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ color: designTokens.colors.primary.main, display: 'flex' }}>
                      {getCategoryIcon(selected, categoryIcons)}
                    </Box>
                    {selected}
                  </Box>
                )}
              >
                <MenuItem value=""><FormattedMessage id="selectCategory" /></MenuItem>
                {sortedCategories.map((category) => (
                  <MenuItem key={category} value={category}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ color: designTokens.colors.text.secondary, display: 'flex' }}>
                        {getCategoryIcon(category, categoryIcons)}
                      </Box>
                      {category}
                    </Box>
                  </MenuItem>
                ))}
                <MenuItem value="new">+ <FormattedMessage id="addNewCategory" /></MenuItem>
              </Select>
            </FormControl>
            <Tooltip title={intl.formatMessage({ id: 'manageCategories' })}>
              <IconButton
                size="small"
                onClick={handleOpenCategoryDialog}
                sx={{
                  background: designTokens.colors.background.elevated,
                  '&:hover': { background: designTokens.colors.background.default }
                }}
              >
                <Settings fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {isAddingCategory && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TextField
                  label={intl.formatMessage({ id: 'newCategory' })}
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  fullWidth
                  variant="outlined"
                  InputProps={{ sx: { borderRadius: designTokens.borderRadius.md } }}
                />
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleAddCategory}
                  sx={{ borderRadius: designTokens.borderRadius.md, textTransform: 'none' }}
                >
                  <FormattedMessage id="add" />
                </Button>
              </Box>
            </motion.div>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Autocomplete
              freeSolo
              options={descriptionSuggestions}
              value={expense.description}
              onChange={(event, newValue) => {
                handleDescriptionChange(event, newValue);
                const normalized = normalizeDescription(newValue || '');
                if (normalized !== newValue) {
                  setExpense(prev => ({ ...prev, description: normalized }));
                }
                const brand = identifyBrand(normalized);
                setIdentifiedBrand(brand);
                if (brand && brand.category && !expense.category) {
                  setExpense(prev => ({ ...prev, category: brand.category || '' }));
                }
              }}
              onInputChange={(event, newInputValue) => {
                setExpense(prev => ({ ...prev, description: newInputValue }));
                const brand = identifyBrand(newInputValue || '');
                setIdentifiedBrand(brand);
                if (brand && brand.category && !expense.category) {
                  setExpense(prev => ({ ...prev, category: brand.category || '' }));
                }
              }}
              filterOptions={filterOptions}
              sx={{ flexGrow: 1 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={intl.formatMessage({ id: 'description' })}
                  name="description"
                  required
                  fullWidth
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    sx: { borderRadius: designTokens.borderRadius.md },
                    autoComplete: 'new-password',
                  }}
                />
              )}
            />
            {identifiedBrand && (
              <motion.div
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                key={identifiedBrand.domain}
              >
                <Box
                  component="img"
                  src={getLogoUrl(identifiedBrand.domain)}
                  alt={identifiedBrand.name}
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '8px',
                    boxShadow: designTokens.shadows.sm,
                    border: `1px solid ${designTokens.colors.background.elevated}`
                  }}
                  onError={(e: any) => {
                    e.target.style.display = 'none';
                  }}
                />
              </motion.div>
            )}
          </Box>

          <DatePicker
            label={intl.formatMessage({ id: 'date' })}
            value={expense.date ? new Date(expense.date) : null}
            onChange={(newValue) => handleDateChange(newValue as Date | null)}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                required
                variant="outlined"
                InputProps={{ ...params.InputProps, sx: { borderRadius: designTokens.borderRadius.md } }}
              />
            )}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormControl fullWidth>
              <InputLabel id="payer-label"><FormattedMessage id="payer" /></InputLabel>
              <Select
                labelId="payer-label"
                label={intl.formatMessage({ id: 'payer' })}
                name="payer"
                value={expense.payer}
                onChange={handleChange}
                required
                sx={{ borderRadius: designTokens.borderRadius.md }}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: payerColors[selected] || '#ccc',
                        border: '1.5px solid rgba(255,255,255,0.2)'
                      }}
                    />
                    {selected}
                  </Box>
                )}
              >
                {sortedPayers.map((payer) => (
                  <MenuItem key={payer} value={payer}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: payerColors[payer] || '#ccc',
                          border: '1.5px solid rgba(255,255,255,0.2)'
                        }}
                      />
                      {payer}
                    </Box>
                  </MenuItem>
                ))}
                <MenuItem value="new">+ <FormattedMessage id="addNewPayer" /></MenuItem>
              </Select>
            </FormControl>
            <Tooltip title={intl.formatMessage({ id: 'managePayers' })}>
              <IconButton
                size="small"
                onClick={handleOpenPayerDialog}
                sx={{
                  background: designTokens.colors.background.elevated,
                  '&:hover': { background: designTokens.colors.background.default }
                }}
              >
                <Settings fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {isAddingPayer && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TextField
                  label={intl.formatMessage({ id: 'newPayer' })}
                  value={newPayer}
                  onChange={(e) => setNewPayer(e.target.value)}
                  fullWidth
                  variant="outlined"
                  InputProps={{ sx: { borderRadius: designTokens.borderRadius.md } }}
                />
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleAddPayer}
                  sx={{ borderRadius: designTokens.borderRadius.md, textTransform: 'none' }}
                >
                  <FormattedMessage id="add" />
                </Button>
              </Box>
            </motion.div>
          )}

          <TextField
            type="text"
            label={`${intl.formatMessage({ id: 'amount' })} (€)`}
            name="amount"
            value={expense.amount}
            onChange={handleAmountChange}
            required
            fullWidth
            variant="outlined"
            InputProps={{
              sx: {
                borderRadius: designTokens.borderRadius.md,
                '& input': { textAlign: 'right', fontWeight: designTokens.typography.fontWeight.bold, fontSize: '1.2rem' }
              },
            }}
            inputProps={{
              inputMode: 'decimal',
              pattern: '[0-9]*[.,]?[0-9]*'
            }}
            onKeyPress={(e) => {
              const pattern = /[0-9.,]/;
              if (!pattern.test(e.key)) {
                e.preventDefault();
              }
            }}
          />

          <FormControl fullWidth>
            <InputLabel id="bank-account-label"><FormattedMessage id="selectBankAccount" /></InputLabel>
            <Select
              labelId="bank-account-label"
              label={intl.formatMessage({ id: 'selectBankAccount' })}
              name="bankAccountId"
              value={expense.bankAccountId}
              onChange={handleChange}
              sx={{ borderRadius: designTokens.borderRadius.md }}
              renderValue={(selected) => {
                const account = bankAccounts.find(a => a.id === selected);
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: account?.color || '#ccc',
                        border: '1.5px solid rgba(255,255,255,0.2)'
                      }}
                    />
                    {account ? account.name : selected}
                  </Box>
                );
              }}
            >
              <MenuItem value=""><FormattedMessage id="noBankAccount" /></MenuItem>
              {bankAccounts.map((account) => (
                <MenuItem key={account.id} value={account.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: account.color || '#ccc',
                        border: '1.5px solid rgba(255,255,255,0.2)'
                      }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', flex: 1, alignItems: 'center' }}>
                      <Typography variant="body2">{account.name}</Typography>
                      <Typography variant="caption" sx={{ color: designTokens.colors.text.secondary, fontWeight: designTokens.typography.fontWeight.bold }}>
                        €{account.balance.toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{
            display: 'flex',
            gap: 2,
            p: 2,
            borderRadius: designTokens.borderRadius.md,
            background: designTokens.colors.background.elevated,
            flexDirection: { xs: 'column', sm: 'row' }
          }}>
            <Box sx={{ flex: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={expense.shared}
                    onChange={handleChange}
                    name="shared"
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Share sx={{ mr: 1, fontSize: 18, color: designTokens.colors.text.secondary }} />
                    <Typography variant="body2"><FormattedMessage id="shared" /></Typography>
                  </Box>
                }
              />
              {expense.shared && (
                <FormControl fullWidth sx={{ mt: 1.5 }}>
                  <InputLabel id="shared-with-label"><FormattedMessage id="sharedWith" /></InputLabel>
                  <Select
                    labelId="shared-with-label"
                    label={intl.formatMessage({ id: 'sharedWith' })}
                    name="sharedWith"
                    value={expense.sharedWith}
                    onChange={handleSharedWithChange}
                    required
                    size="small"
                    sx={{ borderRadius: designTokens.borderRadius.sm }}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: payerColors[selected] || '#ccc',
                            border: '1.5px solid rgba(255,255,255,0.2)'
                          }}
                        />
                        {selected}
                      </Box>
                    )}
                  >
                    <MenuItem value=""><FormattedMessage id="selectPerson" /></MenuItem>
                    {payers.filter(p => p !== expense.payer).map(payer => (
                      <MenuItem key={payer} value={payer}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              backgroundColor: payerColors[payer] || '#ccc',
                              border: '1.5px solid rgba(255,255,255,0.2)'
                            }}
                          />
                          {payer}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>


          </Box>

          {/* Hiding Attach Receipt button as per user request */}
          {/* 
          <Box>
            <Input
              type="file"
              onChange={handleFileChange}
              style={{ display: 'none' }}
              id="receipt-file-input"
            />
            <label htmlFor="receipt-file-input">
              <Button
                variant="outlined"
                component="span"
                startIcon={<Attachment />}
              >
                <FormattedMessage id="attachReceipt" />
              </Button>
            </label>
            {selectedFile && <Typography variant="caption" sx={{ ml: 2 }}>{selectedFile.name}</Typography>}
          </Box>
          */}

          <Button
            type="submit"
            variant="contained"
            startIcon={<Save />}
            disabled={uploading}
            sx={{
              mt: 1,
              py: 1.5,
              borderRadius: designTokens.borderRadius.md,
              background: designTokens.colors.primary.gradient,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: designTokens.typography.fontWeight.bold,
              boxShadow: designTokens.shadows.md
            }}
          >
            {uploading ? intl.formatMessage({ id: 'uploading' }) : intl.formatMessage({ id: 'addExpense' })}
          </Button>
        </Box>

        <Dialog
          open={openCategoryDialog}
          onClose={handleCloseCategoryDialog}
          PaperProps={{
            sx: {
              borderRadius: designTokens.borderRadius.lg,
              background: designTokens.colors.background.paper,
              border: designTokens.glassmorphism.border,
              boxShadow: designTokens.shadows.lg
            }
          }}
        >
          <DialogTitle sx={{ fontWeight: designTokens.typography.fontWeight.bold }}>
            <FormattedMessage id="manageCategories" />
          </DialogTitle>
          <DialogContent>
            <List sx={{ minWidth: 300 }}>
              {sortedCategories.map((category) => (
                <ListItem key={category} sx={{ borderBottom: `1px solid ${designTokens.colors.background.elevated}55` }}>
                  {editingCategoryName === category ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', py: 1 }}>
                      <TextField
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                        fullWidth
                        size="small"
                        variant="outlined"
                        InputProps={{ sx: { borderRadius: designTokens.borderRadius.md } }}
                      />
                      <IconButton onClick={handleEditCategoryConfirm} color="primary" size="small">
                        <Save fontSize="small" />
                      </IconButton>
                      <IconButton onClick={() => setEditingCategoryName('')} size="small">
                        <Cancel fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                        <Box sx={{ color: designTokens.colors.text.secondary, display: 'flex' }}>
                          {getCategoryIcon(category, categoryIcons)}
                        </Box>
                        <ListItemText primary={category} primaryTypographyProps={{ fontWeight: designTokens.typography.fontWeight.medium }} />
                      </Box>
                      <IconButton
                        onClick={() => {
                          setSelectedCategoryForIcon(category);
                          setOpenIconPicker(true);
                        }}
                        size="small"
                        sx={{ color: designTokens.colors.text.secondary }}
                        title={intl.formatMessage({ id: 'selectIcon' })}
                      >
                        <Settings fontSize="small" />
                      </IconButton>
                      <IconButton onClick={() => handleEditCategoryStart(category)} size="small" sx={{ color: designTokens.colors.text.secondary }}>
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton onClick={() => handleDeleteCategoryConfirm(category)} size="small" sx={{ color: designTokens.colors.error.main }}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </>
                  )}
                </ListItem>
              ))}
            </List>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={handleCloseCategoryDialog}
              sx={{ textTransform: 'none', borderRadius: designTokens.borderRadius.md }}
            >
              <FormattedMessage id="close" />
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={openPayerDialog}
          onClose={handleClosePayerDialog}
          PaperProps={{
            sx: {
              borderRadius: designTokens.borderRadius.lg,
              background: designTokens.colors.background.paper,
              border: designTokens.glassmorphism.border,
              boxShadow: designTokens.shadows.lg
            }
          }}
        >
          <DialogTitle sx={{ fontWeight: designTokens.typography.fontWeight.bold }}>
            <FormattedMessage id="managePayers" />
          </DialogTitle>
          <DialogContent>
            <List sx={{ minWidth: 300 }}>
              {sortedPayers.map((payer) => (
                <ListItem key={payer} sx={{ borderBottom: `1px solid ${designTokens.colors.background.elevated}55` }}>
                  {editingPayerName === payer ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', py: 1 }}>
                      <TextField
                        value={newPayerName}
                        onChange={(e) => setNewPayerName(e.target.value)}
                        fullWidth
                        size="small"
                        variant="outlined"
                        InputProps={{ sx: { borderRadius: designTokens.borderRadius.md } }}
                      />
                      <IconButton onClick={handleEditPayerConfirm} color="primary" size="small">
                        <Save fontSize="small" />
                      </IconButton>
                      <IconButton onClick={() => setEditingPayerName('')} size="small">
                        <Cancel fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <>
                      <ListItemText primary={payer} primaryTypographyProps={{ fontWeight: designTokens.typography.fontWeight.medium }} />
                      <IconButton onClick={() => handleEditPayerStart(payer)} size="small" sx={{ color: designTokens.colors.text.secondary }}>
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton onClick={() => handleDeletePayerConfirm(payer)} size="small" sx={{ color: designTokens.colors.error.main }}>
                        <Delete fontSize="small" />
                      </IconButton>
                      <IconButton onClick={() => handleOpenColorPicker(payer)} size="small">
                        <Box style={{ width: 20, height: 20, backgroundColor: payerColors[payer] || '#000000', borderRadius: '50%', border: '2px solid rgba(255,255,255,0.2)' }} />
                      </IconButton>
                    </>
                  )}
                </ListItem>
              ))}
            </List>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={handleClosePayerDialog}
              sx={{ textTransform: 'none', borderRadius: designTokens.borderRadius.md }}
            >
              <FormattedMessage id="close" />
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={openColorPicker}
          onClose={handleCloseColorPicker}
          PaperProps={{
            sx: {
              borderRadius: designTokens.borderRadius.lg,
              background: designTokens.colors.background.paper,
              border: designTokens.glassmorphism.border,
              boxShadow: designTokens.shadows.lg
            }
          }}
        >
          <DialogTitle sx={{ fontWeight: designTokens.typography.fontWeight.bold }}>
            <FormattedMessage id="selectColor" />
          </DialogTitle>
          <DialogContent sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <ChromePicker color={selectedColor} onChange={handleColorChange} />
          </DialogContent>
          <Divider sx={{ opacity: 0.5 }} />
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button
              onClick={handleCloseColorPicker}
              sx={{ textTransform: 'none', borderRadius: designTokens.borderRadius.md }}
            >
              <FormattedMessage id="cancel" />
            </Button>
            <Button
              onClick={handleColorChangeComplete}
              variant="contained"
              sx={{
                textTransform: 'none',
                borderRadius: designTokens.borderRadius.md,
                background: designTokens.colors.primary.gradient,
                boxShadow: designTokens.shadows.md
              }}
            >
              <FormattedMessage id="save" />
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={openIconPicker}
          onClose={() => setOpenIconPicker(false)}
          PaperProps={{
            sx: {
              borderRadius: designTokens.borderRadius.lg,
              background: designTokens.colors.background.paper,
              border: designTokens.glassmorphism.border,
              boxShadow: designTokens.shadows.lg,
              maxWidth: 600
            }
          }}
        >
          <DialogTitle sx={{ fontWeight: designTokens.typography.fontWeight.bold }}>
            <FormattedMessage id="selectIcon" defaultMessage="Selecionar Ícone" />
          </DialogTitle>
          <DialogContent>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(60px, 1fr))',
              gap: 2,
              maxHeight: 400,
              overflowY: 'auto',
              py: 2
            }}>
              {availableIconNames.map((iconName) => (
                <IconButton
                  key={iconName}
                  onClick={() => {
                    if (selectedCategoryForIcon) {
                      onEditCategoryIcon(selectedCategoryForIcon, iconName);
                      setOpenIconPicker(false);
                      setSelectedCategoryForIcon('');
                    }
                  }}
                  sx={{
                    border: categoryIcons[selectedCategoryForIcon] === iconName
                      ? `2px solid ${designTokens.colors.primary.main}`
                      : `1px solid ${designTokens.colors.background.elevated}`,
                    borderRadius: designTokens.borderRadius.md,
                    p: 1.5,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 70,
                    '&:hover': {
                      background: designTokens.colors.background.elevated,
                      borderColor: designTokens.colors.primary.main
                    }
                  }}
                >
                  <Box sx={{ color: designTokens.colors.text.primary, display: 'flex', mb: 0.5 }}>
                    {iconNameMap[iconName]}
                  </Box>
                  <Typography variant="caption" sx={{ fontSize: '0.65rem', textAlign: 'center', wordBreak: 'break-word' }}>
                    {iconName}
                  </Typography>
                </IconButton>
              ))}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() => {
                setOpenIconPicker(false);
                setSelectedCategoryForIcon('');
              }}
              sx={{ textTransform: 'none', borderRadius: designTokens.borderRadius.md }}
            >
              <FormattedMessage id="cancel" />
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={handleCloseSnackbar}>
          <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
            <FormattedMessage id="expenseSubmittedSuccessfully" />
          </Alert>
        </Snackbar>
      </motion.div >
    </LocalizationProvider >
  );
};

export default ExpenseForm;